import { ContentType, Format, type Picture, type UrlsInText } from '@/@types/common'
import type { Comment } from '@/@types/comment'
import type { Post } from '@/@types/post'
import { getComment } from '@/utils/fetchers/comment'
import { fetchAndCachePost } from '@/utils/fetchers/post'
import { JIKE_WEB_HOST } from '@/utils/request/host'
import { toRoutePathParam } from '@/utils/route'
import dayjs from 'dayjs'
import type { ShareImageContent, ShareImageTarget } from './types'

export function isDeleted(data: Post | Comment) {
	return data.deleted || data.status === 'DELETED'
}

export function canShareImage(target: ShareImageTarget) {
	const type = target.kind === 'comment' ? target.data.targetType : target.data.type
	return !isDeleted(target.data) && (type === ContentType.POST || type === ContentType.REPOST)
}

export async function loadShareImageContent({
	target,
	signal,
}: {
	target: ShareImageTarget
	signal: AbortSignal
}): Promise<ShareImageContent> {
	signal.throwIfAborted()
	if (!canShareImage(target)) throw new Error('该内容不支持生成分享图')
	if (target.kind === 'post') {
		const data = await fetchAndCachePost(target.data.id, target.data.type)
		signal.throwIfAborted()
		if (isDeleted(data)) throw new Error('该动态已删除')
		if (data.type === ContentType.REPOST && !data.target) throw new Error('无法读取原动态')
		return { kind: 'post', data }
	}
	const data = await getComment(
		{ id: target.data.id, targetType: target.data.targetType },
		{ suppressToast: true }
	)
	signal.throwIfAborted()
	if (isDeleted(data)) throw new Error('该评论已删除')
	if (data.targetType !== ContentType.POST && data.targetType !== ContentType.REPOST) {
		throw new Error('该评论不支持生成分享图')
	}
	const post = await fetchAndCachePost(data.targetId, data.targetType)
	signal.throwIfAborted()
	return { kind: 'comment', data, post }
}

export function getShareImageURL(content: ShareImageContent) {
	const { data } = content
	const username = content.kind === 'comment' ? content.post.user?.username : data.user?.username
	if (!username) throw new Error('缺少分享链接所需的用户信息')
	const type = content.kind === 'comment' ? content.data.targetType : content.data.type
	if (type !== ContentType.POST && type !== ContentType.REPOST) throw new Error('不支持的分享类型')
	const id = content.kind === 'comment' ? content.data.targetId : content.data.id
	const path = `/u/${encodeURIComponent(username)}/${toRoutePathParam(type)}/${id}`
	const suffix = content.kind === 'comment' ? `/${data.id}` : ''
	return new URL(path + suffix, JIKE_WEB_HOST).href
}

export function getShareText(data: { content: string; urlsInText?: UrlsInText[] }) {
	const replacements = new Map(
		(data.urlsInText ?? [])
			.filter((item) => item.originalUrl)
			.map((item) => [item.originalUrl, item.title])
	)
	if (!replacements.size) return data.content
	const escaped = [...replacements.keys()].map((url) => url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
	return data.content.replace(
		new RegExp(escaped.join('|'), 'g'),
		(url) => replacements.get(url) || url
	)
}

export function getCharacters(text: string) {
	return Array.from(
		new Intl.Segmenter('zh', { granularity: 'grapheme' }).segment(text),
		(part) => part.segment
	)
}

export function getShareDate(value: string) {
	const date = dayjs(value)
	return date.format(date.year() === dayjs().year() ? 'MM/DD' : 'YYYY/MM/DD')
}

export function getPictureURL(picture: Picture) {
	// middlePicUrl supplies a static rendition for animated images when available.
	return picture.format === Format.GIF ? picture.middlePicUrl || picture.picUrl : picture.picUrl
}

export function getPostSummary(post: Post) {
	if (isDeleted(post)) return '原动态已删除'
	return getShareText(post) || post.linkInfo?.title || (post.video ? '分享视频' : '分享图片')
}

export function getAudioCover(post: Post) {
	const audio = post.linkInfo?.audio
	return (
		audio?.originCoverUrl ||
		audio?.coverUrl ||
		(audio?.image && getPictureURL(audio.image)) ||
		post.linkInfo?.pictureUrl
	)
}
